import type { WorkspaceTopicServerMessage } from "./authApi";
import type { MessageRow } from "./db/types";
import {
  envelopeText,
  isWorkspaceServerFileAttachment,
  isWorkspaceServerImageAttachment,
  isWorkspaceServerVoiceAttachment,
  parseMessageEnvelope,
  parseWorkspaceTopicFileEnvelope,
  parseWorkspaceTopicImageEnvelope,
  TOMBSTONE_ENVELOPE_PLAINTEXT,
  workspaceTopicFileEnvelope,
  workspaceTopicImageEnvelope,
  workspaceTopicVoiceEnvelope,
} from "./crypto";
import type { MessageReactionBadge } from "../app/state/domainTypes";

const WORKSPACE_TOPIC_PEER_PREFIX = "wt:";

export function workspaceTopicPeerKey(workspaceId: string, topicId: string): string {
  return `${WORKSPACE_TOPIC_PEER_PREFIX}${workspaceId}:${topicId}`;
}

/** Merge server `reply_to` column into UI envelope (image/file bodies omit reply inside stored JSON). */
export function serverWorkspaceMessageToPlaintext(body: string, replyTo: string | null): string {
  const trimmed = body.trimStart();
  if (trimmed.startsWith("{")) {
    try {
      const j = JSON.parse(body) as { content_type?: string };
      if (j.content_type === "deleted") {
        return TOMBSTONE_ENVELOPE_PLAINTEXT;
      }
    } catch {
      /* fall through */
    }
  }
  const image = parseWorkspaceTopicImageEnvelope(body);
  if (image) {
    return workspaceTopicImageEnvelope(image.caption, image.attachment, replyTo);
  }
  const file = parseWorkspaceTopicFileEnvelope(body);
  if (file) {
    return workspaceTopicFileEnvelope(file.caption, file.attachment, replyTo);
  }
  // Voice: stored body is already { content_type: "voice", ... } — just embed replyTo
  try {
    const j = JSON.parse(body) as { content_type?: string; attachments?: unknown[] };
    if (j.content_type === "voice" && Array.isArray(j.attachments) && j.attachments.length > 0) {
      const att = j.attachments[0];
      if (isWorkspaceServerVoiceAttachment(att)) {
        return workspaceTopicVoiceEnvelope(att, replyTo);
      }
    }
  } catch { /* fall through */ }
  return envelopeText(body, replyTo);
}

/**
 * Self-heal cached workspace plaintext if legacy clients wrapped image/file JSON in text envelope.
 */
export function normalizeWorkspaceTopicCachedPlaintext(peerId: string, plaintext: string): string {
  if (!peerId.startsWith(WORKSPACE_TOPIC_PEER_PREFIX)) return plaintext;

  const env = parseMessageEnvelope(plaintext);
  if (env?.content_type === "image") {
    const first = env.attachments?.[0];
    if (isWorkspaceServerImageAttachment(first)) {
      return workspaceTopicImageEnvelope(env.body ?? "", first, env.reply_to ?? null);
    }
    return plaintext;
  }

  if (env?.content_type === "file") {
    const first = env.attachments?.[0];
    if (isWorkspaceServerFileAttachment(first)) {
      return workspaceTopicFileEnvelope(env.body ?? "", first, env.reply_to ?? null);
    }
    return plaintext;
  }

  if (env?.content_type === "voice") {
    const first = env.attachments?.[0];
    if (isWorkspaceServerVoiceAttachment(first)) {
      return workspaceTopicVoiceEnvelope(first, env.reply_to ?? null);
    }
    return plaintext;
  }

  if (env?.content_type === "text") {
    const nestedImg = parseWorkspaceTopicImageEnvelope(env.body);
    if (nestedImg) {
      return workspaceTopicImageEnvelope(nestedImg.caption, nestedImg.attachment, env.reply_to ?? null);
    }
    const nestedFile = parseWorkspaceTopicFileEnvelope(env.body);
    if (nestedFile) {
      return workspaceTopicFileEnvelope(nestedFile.caption, nestedFile.attachment, env.reply_to ?? null);
    }
    return plaintext;
  }

  const directImg = parseWorkspaceTopicImageEnvelope(plaintext);
  if (directImg) {
    return workspaceTopicImageEnvelope(directImg.caption, directImg.attachment, null);
  }
  const directFile = parseWorkspaceTopicFileEnvelope(plaintext);
  if (directFile) {
    return workspaceTopicFileEnvelope(directFile.caption, directFile.attachment, null);
  }
  return plaintext;
}

export function serverWorkspaceMessageToRow(
  workspaceId: string,
  topicId: string,
  row: WorkspaceTopicServerMessage,
): MessageRow {
  const replyTo = row.reply_to ?? null;
  return {
    id: row.id,
    peerId: workspaceTopicPeerKey(workspaceId, topicId),
    from_id: row.author_id,
    to_id: topicId,
    created_at: row.created_at,
    plaintext: serverWorkspaceMessageToPlaintext(row.body, replyTo),
  };
}

/** Собрать карту реакций из ответа GET messages (для chat state). */
export function workspaceReactionsMapFromServerMessages(
  messages: WorkspaceTopicServerMessage[],
): Record<string, MessageReactionBadge[]> {
  const out: Record<string, MessageReactionBadge[]> = {};
  for (const m of messages) {
    const r = m.reactions;
    if (!r?.length) continue;
    out[m.id] = [...r]
      .map((x) => ({
        emoji: x.emoji,
        count: x.count,
        ownSelected: x.own,
      }))
      .sort((a, b) => {
        if (a.ownSelected !== b.ownSelected) return a.ownSelected ? -1 : 1;
        if (a.count !== b.count) return b.count - a.count;
        return a.emoji.localeCompare(b.emoji);
      });
  }
  return out;
}
